import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import { api } from "../services/api";

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  register: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: true,
  error: null,

  register: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await api.post<{ user: User }>("/auth/register", {
        email,
        password,
      });
      set({ user: res.user, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Registration failed",
        loading: false,
      });
      throw err;
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await api.post<{ user: User }>("/auth/login", {
        email,
        password,
      });
      set({ user: res.user, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Login failed",
        loading: false,
      });
      throw err;
    }
  },

  logout: async () => {
    await api.post("/auth/logout");
    set({ user: null });
  },

  fetchMe: async () => {
    set({ loading: true });
    try {
      const res = await api.get<{ user: User }>("/auth/me");
      set({ user: res.user, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },

  clearError: () => set({ error: null }),
}));

// Helper for selecting multiple fields with useShallow
export function useAuth() {
  return useAuthStore(
    useShallow((s) => ({
      user: s.user,
      loading: s.loading,
      error: s.error,
      register: s.register,
      login: s.login,
      logout: s.logout,
      fetchMe: s.fetchMe,
      clearError: s.clearError,
    }))
  );
}
